'use client';

import { useCallback, useEffect, useMemo, useState, type MouseEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { BookOpen, ChevronLeft, ChevronRight, Edit3, FileText, GraduationCap, Layers, Loader2, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { useAuthContext } from '@/lib/auth-context';
import { hasButtonPermission } from '@/lib/api/permissions';
import { useToast } from '@/lib/hooks/use-toast';
import type { CommunityPost } from '@/lib/community/types';
import {
	deleteCreatorPost,
	deleteMyCollegeCourse,
	deletePublishedSkill,
	fetchCreatorPostDetail,
	fetchCreatorPosts,
	fetchMyCollegeContent,
	fetchPublishedSkillDetail,
	fetchPublishedSkills,
	type MyCollegeContentResp,
	type MyCollegeCourseItem,
	type PublishedSkillItem,
} from './creator-center-api';

type CreatorTab = 'notes' | 'skills' | 'courses';

const PAGE_SIZE = 20;

type DeleteConfirmAnchor = {
	top: number;
	left: number;
	width: number;
	height: number;
};

const TABS: Array<{ value: CreatorTab; label: string; icon: ReactNode; permCode?: string }> = [
	{ value: 'notes', label: '笔记管理', icon: <FileText className="h-4 w-4" /> },
	{ value: 'skills', label: '技能管理', icon: <Layers className="h-4 w-4" />, permCode: 'btn:skill:create' },
	{ value: 'courses', label: '课程管理', icon: <GraduationCap className="h-4 w-4" />, permCode: 'btn:course:create' },
];

function formatDate(value?: string) {
	if (!value) return '-';
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return value;
	return date.toLocaleString('zh-CN', {
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
		hour: '2-digit',
		minute: '2-digit',
	});
}

export function CreatorCenterPage() {
	const navigate = useNavigate();
	const { isLoggedIn, currentUser, openLogin, permissionsReady } = useAuthContext();
	const [activeTab, setActiveTab] = useState<CreatorTab>('notes');

	useEffect(() => {
		if (!isLoggedIn) openLogin();
	}, [isLoggedIn, openLogin]);

	/* ── 根据按钮权限过滤可见 Tab ── */
	const visibleTabs = useMemo(
		() =>
			TABS.filter((tab) => {
				if (!tab.permCode) return true;
				// 权限未加载完成时默认显示，避免闪烁
				if (!permissionsReady) return true;
				return hasButtonPermission(tab.permCode);
			}),
		[permissionsReady],
	);

	// 当前激活的 tab 被权限隐藏时，回退到第一个可见 tab
	useEffect(() => {
		if (visibleTabs.length > 0 && !visibleTabs.some((t) => t.value === activeTab)) {
			setActiveTab(visibleTabs[0].value);
		}
	}, [visibleTabs, activeTab]);

	const canCreateContent = useMemo(() => {
		const current = visibleTabs.find((t) => t.value === activeTab);
		return current ? !current.permCode || hasButtonPermission(current.permCode) : true;
	}, [visibleTabs, activeTab]);

	return (
		<div className="min-h-dvh bg-background pt-14 text-foreground">
			<div className="mx-auto max-w-7xl px-4 py-6 sm:px-6">
				<div className="mb-5 flex flex-wrap items-center justify-between gap-3">
					<div className="flex items-center gap-3">
						<button
							aria-label="返回"
							className="flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-secondary/60 hover:text-foreground"
							onClick={() => navigate(-1)}
							type="button"
						>
							<ChevronLeft className="h-5 w-5" />
						</button>
						<div>
							<h1 className="text-xl font-semibold tracking-normal">创作中心</h1>
							<p className="mt-1 text-xs text-muted-foreground">管理已发布的笔记、技能和学院内容</p>
						</div>
					</div>
					{canCreateContent && (
						<button
							className="inline-flex h-9 items-center gap-2 rounded-full bg-primary px-4 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
							onClick={() => {
								if (!isLoggedIn) {
									openLogin();
									return;
								}
								if (activeTab === 'notes') window.dispatchEvent(new CustomEvent('safeclaw:open-publish'));
								if (activeTab === 'skills') window.dispatchEvent(new CustomEvent('safeclaw:open-skill-publish'));
								if (activeTab === 'courses') window.dispatchEvent(new CustomEvent('safeclaw:open-course-create'));
							}}
							type="button"
						>
							<Plus className="h-4 w-4" />
							新建内容
						</button>
					)}
				</div>

				<div className="grid gap-5 lg:grid-cols-[132px_minmax(0,1fr)]">
					<aside className="liquid-glass-card no-hover h-fit rounded-2xl p-2">
						{visibleTabs.map((tab) => (
							<button
								className={`flex h-10 w-full items-center gap-2 rounded-xl px-3 text-left text-sm transition-colors ${
									activeTab === tab.value ? 'bg-primary/12 font-medium text-primary' : 'text-muted-foreground hover:bg-secondary/60 hover:text-foreground'
								}`}
								key={tab.value}
								onClick={() => setActiveTab(tab.value)}
								type="button"
							>
								{tab.icon}
								{tab.label}
							</button>
						))}
					</aside>

					<main className="min-w-0">
						{activeTab === 'notes' && <NotesPanel currentUserId={currentUser?.userId} isLoggedIn={isLoggedIn} />}
						{activeTab === 'skills' && <SkillsPanel isLoggedIn={isLoggedIn} />}
						{activeTab === 'courses' && <CoursesPanel isLoggedIn={isLoggedIn} />}
					</main>
				</div>
			</div>
		</div>
	);
}

function PanelFrame({
	title,
	subtitle,
	children,
	onRefresh,
	loading,
}: {
	title: string;
	subtitle: string;
	children: ReactNode;
	onRefresh: () => void;
	loading: boolean;
}) {
	return (
		<section className="liquid-glass-card no-hover rounded-2xl p-4 sm:p-5">
			<div className="mb-4 flex flex-wrap items-center justify-between gap-3">
				<div>
					<h2 className="text-base font-semibold">{title}</h2>
					<p className="mt-1 text-xs text-muted-foreground">{subtitle}</p>
				</div>
				<button
					className="inline-flex h-8 items-center gap-2 rounded-full border border-border/60 px-3 text-xs text-muted-foreground transition-colors hover:bg-secondary/60 hover:text-foreground disabled:opacity-50"
					disabled={loading}
					onClick={onRefresh}
					type="button"
				>
					<RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
					刷新
				</button>
			</div>
			{children}
		</section>
	);
}

function NotesPanel({ currentUserId, isLoggedIn }: { currentUserId?: string; isLoggedIn: boolean }) {
	const toast = useToast();
	const [items, setItems] = useState<CommunityPost[]>([]);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(1);
	const [loading, setLoading] = useState(false);
	const [deleting, setDeleting] = useState(false);
	const [deleteTarget, setDeleteTarget] = useState<CommunityPost | null>(null);
	const [deleteAnchor, setDeleteAnchor] = useState<DeleteConfirmAnchor | null>(null);
	const [error, setError] = useState<string | null>(null);

	const load = useCallback(async () => {
		if (!isLoggedIn || !currentUserId) return;
		setLoading(true);
		setError(null);
		try {
			const resp = await fetchCreatorPosts(currentUserId, page, PAGE_SIZE);
			setItems(resp.items);
			setTotal(resp.total);
		} catch (err) {
			setError(err instanceof Error ? err.message : '笔记加载失败');
		} finally {
			setLoading(false);
		}
	}, [currentUserId, isLoggedIn, page]);

	useEffect(() => {
		load();
	}, [load]);

	useEffect(() => {
		window.addEventListener('safeclaw:creator-content-changed', load);
		return () => window.removeEventListener('safeclaw:creator-content-changed', load);
	}, [load]);

	async function handleEdit(id: string) {
		try {
			const detail = await fetchCreatorPostDetail(id);
			window.dispatchEvent(new CustomEvent('safeclaw:open-publish', { detail: { editPost: detail } }));
		} catch (err) {
			toast.error(err instanceof Error ? err.message : '详情加载失败');
		}
	}

	async function handleDelete(id: string) {
		try {
			setDeleting(true);
			await deleteCreatorPost(id);
			toast.success('笔记已删除');
			setDeleteTarget(null);
			setDeleteAnchor(null);
			load();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : '删除失败');
		} finally {
			setDeleting(false);
		}
	}

	return (
		<PanelFrame loading={loading} onRefresh={load} subtitle="" title="笔记管理">
			<DataState empty={items.length === 0} error={error} loading={loading}>
				<div className="overflow-x-auto">
					<div className="min-w-[760px] overflow-visible rounded-xl border border-border/60">
						<TableHeader columns="grid-cols-[1.7fr_1fr_0.7fr_0.9fr_92px]" labels={['标题', '板块类型', '互动', '更新时间', '操作']} />
						{items.map((item) => (
							<div className="grid grid-cols-[1.7fr_1fr_0.7fr_0.9fr_92px] items-center border-t border-border/60 px-4 py-3 text-xs" key={item.id}>
								<div className="min-w-0">
									<p className="truncate text-sm font-medium">{item.title}</p>
									<p className="mt-1 line-clamp-1 text-muted-foreground">{item.description}</p>
								</div>
								<span className="text-muted-foreground">{item.abilityType}</span>
								<span className="text-muted-foreground">
									{item.stats.likes} 赞 · {item.stats.comments} 评
								</span>
								<span className="text-muted-foreground">{formatDate(item.updatedAt)}</span>
								<RowActions
									confirm={
										deleteTarget?.id === item.id ? (
											<DeleteConfirmDialog
												anchor={deleteAnchor}
												deleting={deleting}
												onCancel={() => {
													if (deleting) return;
													setDeleteTarget(null);
													setDeleteAnchor(null);
												}}
												onConfirm={() => handleDelete(deleteTarget.id)}
											/>
										) : null
									}
									onDelete={(event) => {
										setDeleteTarget(item);
										setDeleteAnchor(getDeleteAnchor(event));
									}}
									onEdit={() => handleEdit(item.id)}
								/>
							</div>
						))}
					</div>
				</div>
				<Pagination page={page} pageSize={PAGE_SIZE} setPage={setPage} total={total} />
			</DataState>
		</PanelFrame>
	);
}

function SkillsPanel({ isLoggedIn }: { isLoggedIn: boolean }) {
	const toast = useToast();
	const [items, setItems] = useState<PublishedSkillItem[]>([]);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(1);
	const [loading, setLoading] = useState(false);
	const [deleting, setDeleting] = useState(false);
	const [deleteTarget, setDeleteTarget] = useState<PublishedSkillItem | null>(null);
	const [deleteAnchor, setDeleteAnchor] = useState<DeleteConfirmAnchor | null>(null);
	const [error, setError] = useState<string | null>(null);

	const load = useCallback(async () => {
		if (!isLoggedIn) return;
		setLoading(true);
		setError(null);
		try {
			const resp = await fetchPublishedSkills(page, PAGE_SIZE);
			setItems(resp.items);
			setTotal(resp.total);
		} catch (err) {
			setError(err instanceof Error ? err.message : '技能加载失败');
		} finally {
			setLoading(false);
		}
	}, [isLoggedIn, page]);

	useEffect(() => {
		load();
	}, [load]);

	useEffect(() => {
		window.addEventListener('safeclaw:creator-content-changed', load);
		return () => window.removeEventListener('safeclaw:creator-content-changed', load);
	}, [load]);

	async function handleEdit(id: number) {
		try {
			const detail = await fetchPublishedSkillDetail(id);
			window.dispatchEvent(new CustomEvent('safeclaw:open-skill-publish', { detail: { editSkill: detail } }));
		} catch (err) {
			toast.error(err instanceof Error ? err.message : '详情加载失败');
		}
	}

	async function handleDelete(id: number) {
		try {
			setDeleting(true);
			await deletePublishedSkill(id);
			toast.success('技能已删除');
			setDeleteTarget(null);
			setDeleteAnchor(null);
			load();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : '删除失败');
		} finally {
			setDeleting(false);
		}
	}

	return (
		<PanelFrame loading={loading} onRefresh={load} subtitle="" title="技能管理">
			<DataState empty={items.length === 0} error={error} loading={loading}>
				<div className="overflow-x-auto">
					<div className="min-w-[820px] overflow-visible rounded-xl border border-border/60">
						<TableHeader columns="grid-cols-[1.4fr_1fr_0.7fr_0.7fr_0.9fr_92px]" labels={['名称', '分类', '使用数', '版本', '更新时间', '操作']} />
						{items.map((item) => (
							<div className="grid grid-cols-[1.4fr_1fr_0.7fr_0.7fr_0.9fr_92px] items-center border-t border-border/60 px-4 py-3 text-xs" key={item.id}>
								<div className="min-w-0">
									<p className="truncate text-sm font-medium">{item.name}</p>
									<p className="mt-1 line-clamp-1 text-muted-foreground">{item.description}</p>
								</div>
								<span className="truncate text-muted-foreground">{item.category || '-'}</span>
								<span className="text-muted-foreground">{item.useNum}</span>
								<span className="text-muted-foreground">{item.version || '-'}</span>
								<span className="text-muted-foreground">{formatDate(item.updatedAt)}</span>
								<RowActions
									confirm={
										deleteTarget?.id === item.id ? (
											<DeleteConfirmDialog
												anchor={deleteAnchor}
												deleting={deleting}
												onCancel={() => {
													if (deleting) return;
													setDeleteTarget(null);
													setDeleteAnchor(null);
												}}
												onConfirm={() => handleDelete(deleteTarget.id)}
											/>
										) : null
									}
									onDelete={(event) => {
										setDeleteTarget(item);
										setDeleteAnchor(getDeleteAnchor(event));
									}}
									onEdit={() => handleEdit(item.id)}
								/>
							</div>
						))}
					</div>
				</div>
				<Pagination page={page} pageSize={PAGE_SIZE} setPage={setPage} total={total} />
			</DataState>
		</PanelFrame>
	);
}

function CoursesPanel({ isLoggedIn }: { isLoggedIn: boolean }) {
	const toast = useToast();
	const [data, setData] = useState<MyCollegeContentResp | null>(null);
	const [page, setPage] = useState(1);
	const [loading, setLoading] = useState(false);
	const [deleting, setDeleting] = useState(false);
	const [deleteTarget, setDeleteTarget] = useState<MyCollegeCourseItem | null>(null);
	const [deleteAnchor, setDeleteAnchor] = useState<DeleteConfirmAnchor | null>(null);
	const [error, setError] = useState<string | null>(null);

	const load = useCallback(async () => {
		if (!isLoggedIn) return;
		setLoading(true);
		setError(null);
		try {
			setData(await fetchMyCollegeContent(page, PAGE_SIZE));
		} catch (err) {
			setError(err instanceof Error ? err.message : '课程加载失败');
		} finally {
			setLoading(false);
		}
	}, [isLoggedIn, page]);

	useEffect(() => {
		load();
	}, [load]);

	useEffect(() => {
		window.addEventListener('safeclaw:creator-content-changed', load);
		return () => window.removeEventListener('safeclaw:creator-content-changed', load);
	}, [load]);

	const courses = data?.courses?.list ?? [];
	const chapters = data?.chapters?.list ?? [];
	const steps = data?.steps?.list ?? [];
	const courseTotal = data?.courses?.total ?? 0;
	const getCourseStats = useCallback(
		(courseId: number) => {
			const courseChapters = chapters.filter((chapter) => chapter.courseId === courseId);
			const chapterIds = new Set(courseChapters.map((chapter) => chapter.id));
			return {
				chapterCount: courseChapters.length,
				stepCount: steps.filter((step) => chapterIds.has(step.chapterId)).length,
			};
		},
		[chapters, steps],
	);

	async function handleCourseDelete(course: MyCollegeCourseItem) {
		try {
			setDeleting(true);
			await deleteMyCollegeCourse(course.id);
			toast.success('课程已删除');
			setDeleteTarget(null);
			setDeleteAnchor(null);
			if (courses.length === 1 && page > 1) {
				setPage(page - 1);
			} else {
				load();
			}
		} catch (err) {
			toast.error(err instanceof Error ? err.message : '删除失败');
		} finally {
			setDeleting(false);
		}
	}

	function handleCourseEdit(course: MyCollegeCourseItem) {
		const courseChapters = chapters.filter((chapter) => chapter.courseId === course.id).sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
		const chapterIds = new Set(courseChapters.map((chapter) => chapter.id));
		const courseSteps = steps.filter((step) => chapterIds.has(step.chapterId)).sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

		window.dispatchEvent(
			new CustomEvent('safeclaw:open-course-create', {
				detail: {
					editCourse: {
						course,
						chapters: courseChapters,
						steps: courseSteps,
					},
				},
			}),
		);
	}

	return (
		<PanelFrame loading={loading} onRefresh={load} subtitle="" title="课程管理">
			<DataState empty={courseTotal === 0} error={error} loading={loading}>
				<div className="overflow-visible rounded-xl border border-border/60">
					<div className="divide-y divide-border/60">
						{courses.map((course) => (
							<CourseCard
								confirm={
									deleteTarget?.id === course.id ? (
										<DeleteConfirmDialog
											anchor={deleteAnchor}
											deleting={deleting}
											onCancel={() => {
												if (deleting) return;
												setDeleteTarget(null);
												setDeleteAnchor(null);
											}}
											onConfirm={() => handleCourseDelete(deleteTarget)}
										/>
									) : null
								}
								course={course}
								key={course.id}
								onDelete={(event) => {
									setDeleteTarget(course);
									setDeleteAnchor(getDeleteAnchor(event));
								}}
								onEdit={() => handleCourseEdit(course)}
								stats={getCourseStats(course.id)}
							/>
						))}
					</div>
				</div>
				<Pagination page={page} pageSize={PAGE_SIZE} setPage={setPage} total={courseTotal} />
			</DataState>
		</PanelFrame>
	);
}

function CourseCard({
	confirm,
	course,
	stats,
	onEdit,
	onDelete,
}: {
	confirm?: ReactNode;
	course: MyCollegeCourseItem;
	stats: { chapterCount: number; stepCount: number };
	onEdit: () => void;
	onDelete: (event: MouseEvent<HTMLButtonElement>) => void;
}) {
	return (
		<article className="group relative grid grid-cols-[112px_minmax(0,1fr)] bg-card/40 text-left transition-colors hover:bg-secondary/25">
			<div className="relative h-full min-h-[108px] overflow-hidden bg-secondary/45">
				{course.coverImage ? (
					<img alt={course.title} className="h-full w-full object-cover" src={course.coverImage} />
				) : (
					<div className="flex h-full w-full items-center justify-center text-muted-foreground/60">
						<BookOpen className="h-8 w-8" />
					</div>
				)}
			</div>
			<div className="min-w-0 p-4">
				<div className="flex items-start justify-between gap-2">
					<div className="min-w-0">
						<div className="flex items-center gap-2">
							<h3 className="truncate text-sm font-semibold leading-5">{course.title}</h3>
							<span
								className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] ${course.status === 1 ? 'bg-emerald-500/10 text-emerald-600' : 'bg-secondary/80 text-muted-foreground'}`}
							>
								{course.status === 1 ? '已发布' : '草稿'}
							</span>
						</div>
						<p className="mt-1 line-clamp-2 text-xs leading-5 text-muted-foreground">{course.description || course.intro || '-'}</p>
					</div>
					<div className="relative flex shrink-0 items-center gap-1">
						<button
							aria-label="编辑课程"
							className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-secondary/70 hover:text-foreground"
							onClick={(event) => {
								event.stopPropagation();
								onEdit();
							}}
							type="button"
						>
							<Edit3 className="h-3.5 w-3.5" />
						</button>
						<button
							aria-label="删除课程"
							className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
							onClick={(event) => {
								event.stopPropagation();
								onDelete(event);
							}}
							type="button"
						>
							<Trash2 className="h-3.5 w-3.5" />
						</button>
						{confirm}
					</div>
				</div>
				<div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
					<span className="rounded-md bg-secondary/45 px-2 py-1">{stats.chapterCount} 章节</span>
					<span className="rounded-md bg-secondary/45 px-2 py-1">{stats.stepCount} 步骤</span>
					<span className="ml-auto text-muted-foreground/70">{formatDate(course.createdAt)}</span>
				</div>
			</div>
		</article>
	);
}

function DataState({ loading, error, empty, children }: { loading: boolean; error: string | null; empty: boolean; children: ReactNode }) {
	if (loading) {
		return (
			<div className="flex min-h-[280px] items-center justify-center text-sm text-muted-foreground">
				<Loader2 className="mr-2 h-4 w-4 animate-spin" />
				加载中
			</div>
		);
	}
	if (error) {
		return <div className="rounded-xl border border-destructive/20 bg-destructive/5 p-6 text-center text-sm text-destructive">{error}</div>;
	}
	if (empty) {
		return <div className="rounded-xl border border-border/60 bg-secondary/25 p-12 text-center text-sm text-muted-foreground">暂无内容</div>;
	}
	return <>{children}</>;
}

function TableHeader({ columns, labels }: { columns: string; labels: string[] }) {
	return (
		<div className={`grid ${columns} bg-secondary/55 px-4 py-3 text-xs text-muted-foreground`}>
			{labels.map((label) => (
				<span key={label}>{label}</span>
			))}
		</div>
	);
}

function RowActions({ confirm, onEdit, onDelete }: { confirm?: ReactNode; onEdit: () => void; onDelete: (event: MouseEvent<HTMLButtonElement>) => void }) {
	return (
		<div className="relative flex items-center gap-1">
			<button
				aria-label="编辑"
				className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-secondary/70 hover:text-foreground"
				onClick={onEdit}
				type="button"
			>
				<Edit3 className="h-3.5 w-3.5" />
			</button>
			<button
				aria-label="删除"
				className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
				onClick={onDelete}
				type="button"
			>
				<Trash2 className="h-3.5 w-3.5" />
			</button>
			{confirm}
		</div>
	);
}

function DeleteConfirmDialog({
	anchor,
	deleting,
	onCancel,
	onConfirm,
}: {
	anchor: DeleteConfirmAnchor | null;
	deleting: boolean;
	onCancel: () => void;
	onConfirm: () => void;
}) {
	if (!anchor) return null;

	const width = 176;
	const gap = 8;
	const viewportWidth = typeof window === 'undefined' ? 1440 : window.innerWidth;
	const left = Math.min(Math.max(anchor.left + anchor.width / 2 - width / 2, 12), viewportWidth - width - 12);
	const top = anchor.top + anchor.height + gap;
	const arrowLeft = Math.min(Math.max(anchor.left + anchor.width / 2 - left, 18), width - 18);

	return createPortal(
		<div
			aria-modal="false"
			className="fixed z-[1000] w-[176px] rounded-md border border-border/70 bg-background px-3 py-2 text-left shadow-md"
			role="dialog"
			style={{ left, top }}
		>
			<span className="absolute -top-1 h-2 w-2 -translate-x-1/2 rotate-45 border-l border-t border-border/70 bg-background" style={{ left: arrowLeft }} />
			<p className="text-center text-sm leading-5 text-foreground">确定删除吗？</p>
			<div className="mt-2.5 flex justify-center gap-2">
				<button
					className="h-7 rounded px-2.5 text-xs text-muted-foreground transition-colors hover:bg-secondary/70 hover:text-foreground disabled:opacity-50"
					disabled={deleting}
					onClick={onCancel}
					type="button"
				>
					取消
				</button>
				<button
					className="inline-flex h-7 min-w-[48px] items-center justify-center gap-1 rounded bg-destructive px-2.5 text-xs font-medium text-white transition-colors hover:bg-destructive/90 disabled:opacity-60"
					disabled={deleting}
					onClick={onConfirm}
					type="button"
				>
					{deleting && <Loader2 className="h-3 w-3 animate-spin" />}
					{deleting ? '删除中' : '确定'}
				</button>
			</div>
		</div>,
		document.body,
	);
}

function getDeleteAnchor(event: MouseEvent<HTMLButtonElement>): DeleteConfirmAnchor {
	const rect = event.currentTarget.getBoundingClientRect();
	return {
		top: rect.top,
		left: rect.left,
		width: rect.width,
		height: rect.height,
	};
}

function Pagination({ total, page, pageSize, setPage }: { total: number; page: number; pageSize: number; setPage: (page: number) => void }) {
	const totalPages = Math.max(1, Math.ceil(total / pageSize));
	return (
		<div className="mt-4 flex items-center justify-end gap-2 text-xs text-muted-foreground">
			<span>共 {total} 条</span>
			<button
				className="flex h-7 w-7 items-center justify-center rounded border border-border/70 disabled:opacity-40"
				disabled={page <= 1}
				onClick={() => setPage(page - 1)}
				type="button"
			>
				<ChevronLeft className="h-3.5 w-3.5" />
			</button>
			<span className="flex h-7 min-w-7 items-center justify-center rounded border border-primary px-2 text-primary">{page}</span>
			<button
				className="flex h-7 w-7 items-center justify-center rounded border border-border/70 disabled:opacity-40"
				disabled={page >= totalPages}
				onClick={() => setPage(page + 1)}
				type="button"
			>
				<ChevronRight className="h-3.5 w-3.5" />
			</button>
		</div>
	);
}
